import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';

/**
 * The Braze Web SDK is loaded by the Braze app embed and exposed on `window`. The embed
 * only creates `window.braze` after the Braze web pixel writes `brazeAppSettings` to
 * localStorage (it checks after ~2s, then retries for up to 5s), so poll before giving up.
 *
 * @param {number} [timeout]
 * @returns {Promise<any | undefined>}
 */
const getBraze = async (timeout = 10000) => {
  const start = Date.now();
  while (!(/** @type {any} */ (window).braze) && Date.now() - start < timeout) {
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  return /** @type {any} */ (window).braze;
};

/**
 * Back in stock dialog trigger. Only opens once the shopper has an explicit,
 * complete variant selection — otherwise shows the same required-attributes
 * error as Add to Bag, matching the sibling CTA's validation behavior.
 *
 * @extends {DialogComponent}
 */
class BackInStockDialog extends DialogComponent {
  /**
   * @param {string|undefined} [withoutBackdrop]
   */
  showDialog(withoutBackdrop) {
    const productForm = this.closest('product-form-component');

    if (productForm?.hasCompleteVariantSelection && !productForm.hasCompleteVariantSelection()) {
      productForm.showRequiredSelectionError();
      return;
    }

    super.showDialog(withoutBackdrop);
  }
}

if (!customElements.get('back-in-stock-dialog')) {
  customElements.define('back-in-stock-dialog', BackInStockDialog);
}

/**
 * Back in stock sign-up form. Subscriptions are sent through the Braze Web SDK.
 *
 * Two views live inside the same dialog: the sign-up form and a "Thank you" confirmation.
 * Submitting swaps to the confirmation view; reopening the dialog resets back to the form.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} email - Email input.
 * @property {HTMLInputElement} [consent] - Optional consent checkbox.
 * @property {HTMLInputElement} [phone] - Phone number input, only rendered when the
 *   "Enable SMS notifications" theme setting is on. Progressively enhanced into an
 *   intl-tel-input widget with a country-flag dropdown once the dialog is opened.
 * @property {HTMLInputElement} [smsConsent] - Optional SMS consent checkbox, required only when a phone number is provided.
 * @property {HTMLElement} error - Error message container.
 * @property {HTMLElement} errorText - Error message text node.
 * @property {HTMLButtonElement} submit - Sign-up submit button.
 * @property {HTMLElement} formView - Sign-up form view.
 * @property {HTMLElement} formHeading - Sign-up form heading (for aria-labelledby).
 * @property {HTMLElement} successView - Thank-you confirmation view.
 * @property {HTMLElement} successHeading - Thank-you heading (for aria-labelledby + focus).
 * @property {HTMLButtonElement} done - Done button on the confirmation view.
 *
 * @extends {Component<Refs>}
 */
class BackInStockForm extends Component {
  requiredRefs = [
    'email',
    'error',
    'errorText',
    'submit',
    'formView',
    'formHeading',
    'successView',
    'successHeading',
    'done',
  ];

  #abortController = new AbortController();

  /** @type {Promise<void> | null} - Resolves once the vendored intl-tel-input JS/CSS have loaded. */
  #assetsPromise = null;

  /**
   * The vendored intl-tel-input instance (not an installed npm dependency, so its
   * types aren't resolvable here). See `#initPhoneInput` for the shape used.
   * @type {{promise: Promise<unknown>, isValidNumber: () => (boolean|null|undefined), getNumber: () => string, destroy: () => void} | null}
   */
  #iti = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Submit on Enter from the email field.
    this.refs.email?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.handleSubmit();
        }
      },
      { signal }
    );

    // Reset to the sign-up view each time the dialog is (re)opened + lazy intl tel input initialization.
    this.closest('back-in-stock-dialog')?.addEventListener(
      'dialog:open',
      () => {
        this.#resetView();
        this.#initPhoneInput();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#iti?.destroy();
    this.#iti = null;
  }

  /**
   * Closes the dialog from the confirmation view.
   */
  handleDone = () => {
    this.closest('back-in-stock-dialog')?.closeDialog();
  };

  /**
   * Validates the form and, if valid, submits the notification request.
   */
  handleSubmit = async () => {
    const { email, consent, phone, smsConsent, submit } = this.refs;
    const value = email.value.trim();
    // No phone field at all when SMS notifications are off.
    const smsEnabled = this.dataset.smsEnabled === 'true';
    const phoneValue = smsEnabled ? phone?.value.trim() || '' : '';

    this.#clearMessages();

    const consentRequired = this.dataset.consentRequired === 'true';

    if (!value) {
      this.#showError(this.dataset.errorRequired, true);
      email.focus();
      return;
    }

    if (!BackInStockForm.#isValidEmail(value)) {
      this.#showError(this.dataset.errorEmail, true);
      email.focus();
      return;
    }

    if (consentRequired && !consent?.checked) {
      this.#showError(this.dataset.errorConsent);
      consent?.setAttribute('aria-invalid', 'true');
      consent?.focus();
      return;
    }

    if (phoneValue) {
      const isValid = await this.#isPhoneNumberValid(phoneValue);
      if (!isValid) {
        this.#showError(this.dataset.errorPhone, false, true);
        phone?.focus();
        return;
      }
    }

    if (phoneValue && smsConsent && !smsConsent.checked) {
      // Shares the same copy as the email consent checkbox's required-field error.
      this.#showError(this.dataset.errorConsent);
      smsConsent.setAttribute('aria-invalid', 'true');
      smsConsent.focus();
      return;
    }

    submit.disabled = true;

    try {
      await this.sendToBraze({
        email: value,
        phone: phoneValue ? this.#getFormattedPhoneNumber(phoneValue) : undefined,
        product_id: this.dataset.productId,
        variant_id: this.dataset.variantId,
        product_handle: this.dataset.productHandle,
        product_title: this.dataset.productTitle,
        product_sku: this.dataset.productSku,
        variant_sku: this.dataset.variantSku,
        market_id: this.dataset.marketId,
        market_handle: this.dataset.marketHandle,
        country: this.dataset.country,
        language: this.dataset.language,
        currency: this.dataset.currency,
        customer_id: this.dataset.customerId,
        customer_email: this.dataset.customerEmail,
        consent: Boolean(consent?.checked),
        sms_consent: smsEnabled ? Boolean(phoneValue && smsConsent?.checked) : undefined,
      });
      this.#showSuccess();
    } catch (error) {
      console.error('[back-in-stock]', error);
      this.#showError(this.dataset.errorGeneric);
      submit.disabled = false;
    }
  };


  /**
   * Subscribes the shopper to back-in-stock notifications via the Braze Web SDK: sets their
   * email/phone on the current Braze user, then logs the catalog's subscribe event.
   * Rejects if the SDK hasn't loaded so the caller can show the generic error.
   *
   * @param {Record<string, string | boolean | undefined>} payload
   * @returns {Promise<void>}
   */
  async sendToBraze(payload) {
    const braze = await getBraze();
    if (!braze) {
      throw new Error(
        'Braze Web SDK is not loaded. Check the Braze app embed is enabled, tracking consent was given, and SDK load timing is "upon site visit".'
      );
    }

    const user = braze.getUser();
    if (!user) throw new Error('Braze user is unavailable — is the SDK initialized?');

    const itemId = payload.variant_id || payload.product_id;
    if (!itemId) throw new Error('Missing variant/product ID for the Braze catalog item.');

    // Configured under Settings > Braze in the theme editor; all three are required, and
    // must match the "Back in stock" settings on the Braze catalog.
    const eventName = this.dataset.brazeEvent?.trim();
    const itemIdProperty = this.dataset.brazeItemIdProperty?.trim();
    const catalogName = this.dataset.brazeCatalog?.trim();

    if (!eventName || !itemIdProperty || !catalogName) {
      throw new Error('Missing Braze theme settings: event name, catalog name and catalog item ID property.');
    }

    if (payload.email) user.setEmail(payload.email);
    if (payload.phone) user.setPhoneNumber(payload.phone);

    // Braze rejects events with undefined property values, so drop empty fields.
    const { email, phone, customer_email, ...properties } = payload;
    const eventProperties = {
      ...Object.fromEntries(Object.entries(properties).filter(([, value]) => value !== undefined && value !== '')),
      // Item ID must be a string to match the `id` type in the catalog.
      [itemIdProperty]: String(itemId),
      catalog_name: catalogName,
      type: ['back_in_stock'],
    };

    const logged = braze.logCustomEvent(eventName, eventProperties);
    if (logged === false) {
      throw new Error(`Braze rejected "${eventName}" with properties ${JSON.stringify(eventProperties)}`);
    }

    braze.requestImmediateDataFlush();
  }

  /**
   * Lazily loads the vendored intl-tel-input script + stylesheet (only once, and only
   * once the shopper actually opens the dialog). Falls back to a plain input if the assets fail to load.
   *
   * @returns {Promise<void>}
   */
  #initPhoneInput() {
    // Nothing to enhance when SMS notifications are off — the field isn't rendered.
    if (this.#iti || !this.refs.phone) return Promise.resolve();

    return this.#loadItiAssets()
      .then(() => {
        const { phone } = this.refs;
        const utilsUrl = this.dataset.itiUtilsUrl;
        const initialCountry = (this.dataset.country || '').toLowerCase();
        const onlyCountries = (this.dataset.itiOnlyCountries || '')
          .split(',')
          .map((code) => code.trim())
          .filter(Boolean);

        this.#iti = window.intlTelInput(phone, {
          initialCountry: initialCountry || undefined,
          // Limits the flag dropdown to the site's active markets instead of every country.
          onlyCountries: onlyCountries.length ? onlyCountries : undefined,
          strictMode: true,
          loadUtils: utilsUrl ? () => import(utilsUrl) : undefined,
        });
      })
      .catch(() => {
        // Library failed to load (offline, blocked, etc.) — #isPhoneNumberValid falls
        // back to a basic regex check, and the field stays a plain text input.
      });
  }

  /**
   * Injects the vendored intl-tel-input <link> and <script> tags once, resolving when
   * the script has finished executing (and window.intlTelInput is available).
   *
   * @returns {Promise<void>}
   */
  #loadItiAssets() {
    if (this.#assetsPromise) return this.#assetsPromise;

    const cssHref = this.dataset.itiCssUrl;
    const jsSrc = this.dataset.itiScriptUrl;

    this.#assetsPromise = new Promise((resolve, reject) => {
      if (!jsSrc) {
        reject(new Error('Missing intl-tel-input script URL.'));
        return;
      }

      if (cssHref && !document.querySelector(`link[href="${cssHref}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssHref;
        document.head.appendChild(link);
      }

      if (window.intlTelInput) {
        resolve();
        return;
      }

      const existingScript = document.querySelector(`script[src="${jsSrc}"]`);
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Failed to load intl-tel-input.')), {
          once: true,
        });
        return;
      }

      const script = document.createElement('script');
      script.src = jsSrc;
      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener('error', () => reject(new Error('Failed to load intl-tel-input.')), { once: true });
      document.head.appendChild(script);
    });

    return this.#assetsPromise;
  }

  /**
   * Validates a phone number against the currently selected country in the
   * intl-tel-input widget. Falls back to a basic regex when the library couldn't
   * be loaded (e.g. offline).
   *
   * @param {string} phoneValue
   * @returns {Promise<boolean>}
   */
  async #isPhoneNumberValid(phoneValue) {
    if (!this.#iti) return BackInStockForm.#isValidPhone(phoneValue);

    await this.#iti.promise; // resolves once the validation utils have loaded
    return this.#iti.isValidNumber() ?? BackInStockForm.#isValidPhone(phoneValue);
  }

  /**
   * @param {string} phoneValue
   * @returns {string} - The phone number in E.164 format when the widget is available,
   *   otherwise the raw value as typed.
   */
  #getFormattedPhoneNumber(phoneValue) {
    return this.#iti?.getNumber() || phoneValue;
  }

  static #isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Accepts digits with optional spaces, dashes, parentheses, and a leading "+".
   * Requires at least 7 digits so obviously incomplete numbers are rejected.
   * Only used as a fallback if intl-tel-input fails to load.
   *
   * @param {string} phone
   */
  static #isValidPhone(phone) {
    if (!/^\+?[0-9\s\-().]+$/.test(phone)) return false;
    const digitCount = (phone.match(/\d/g) || []).length;
    return digitCount >= 7;
  }

  /**
   * @param {string} [message]
   * @param {boolean} [invalidEmail] - Whether to flag the email field as invalid.
   * @param {boolean} [invalidPhone] - Whether to flag the phone field as invalid.
   */
  #showError(message, invalidEmail = false, invalidPhone = false) {
    const { error, errorText, email, phone } = this.refs;
    // reveal before setting text so the alert is announced
    error.hidden = false;
    error.classList.remove('hidden');
    errorText.textContent = message || this.dataset.errorGeneric || '';
    if (invalidEmail) email.setAttribute('aria-invalid', 'true');
    if (invalidPhone) phone?.setAttribute('aria-invalid', 'true');
  }

  /**
   * Swaps the dialog from the sign-up form to the "Thank you" confirmation view.
   */
  #showSuccess() {
    const { formView, successView, successHeading } = this.refs;
    formView.hidden = true;
    successView.hidden = false;
    this.#setDialogLabelledby(successHeading.id);
    // move focus to the confirmation for a11y
    successHeading.focus();
  }

  /**
   * Resets the dialog back to a fresh sign-up form. Kept as an arrow function since
   * it's invoked from the dialog:open handler and needs to keep `this`.
   */
  #resetView = () => {
    const { formView, successView, formHeading, email, consent, phone, smsConsent, error, submit } = this.refs;
    formView.hidden = false;
    successView.hidden = true;
    this.#setDialogLabelledby(formHeading.id);

    email.value = '';
    email.disabled = false;
    email.removeAttribute('aria-invalid');
    if (consent) {
      consent.checked = false;
      consent.removeAttribute('aria-invalid');
    }
    if (phone) {
      phone.value = '';
      phone.removeAttribute('aria-invalid');
    }
    if (smsConsent) {
      smsConsent.checked = false;
      smsConsent.removeAttribute('aria-invalid');
    }
    submit.disabled = false;

    error.hidden = true;
    error.classList.add('hidden');

    // showModal() autofocuses the close button and iOS draws a focus box on the X —
    // move focus to the heading instead (same pattern as the thank-you view).
    requestAnimationFrame(() => {
      formHeading.focus({ preventScroll: true });
    });
  };

  #clearMessages() {
    const { error, email, consent, phone, smsConsent } = this.refs;
    error.hidden = true;
    error.classList.add('hidden');
    email.removeAttribute('aria-invalid');
    consent?.removeAttribute('aria-invalid');
    phone?.removeAttribute('aria-invalid');
    smsConsent?.removeAttribute('aria-invalid');
  }

  /**
   * @param {string} id
   */
  #setDialogLabelledby(id) {
    this.closest('dialog')?.setAttribute('aria-labelledby', id);
  }
}

if (!customElements.get('back-in-stock-form')) {
  customElements.define('back-in-stock-form', BackInStockForm);
}
